'use client';

import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Satellite {
  id: string;
  name: string;
  angle: number;
  speed: number;
  radius: number;
  color: string;
}

interface HeroSectionProps {
  onExploreClick: () => void;
  onInvestmentClick: () => void;
}

const HeroSection = ({ onExploreClick, onInvestmentClick }: HeroSectionProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();
  const [isHydrated, setIsHydrated] = useState(false);

  const satellites: Satellite[] = [
    { id: 'sat1', name: 'Infrastructure', angle: 0, speed: 0.01, radius: 150, color: '#FFD700' },
    { id: 'sat2', name: 'Research', angle: 45, speed: 0.012, radius: 150, color: '#00D4AA' },
    { id: 'sat3', name: 'Innovation', angle: 90, speed: 0.009, radius: 150, color: '#FF6B35' },
    { id: 'sat4', name: 'Coordination', angle: 135, speed: 0.011, radius: 150, color: '#FFD700' },
  ];

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    const resizeCanvas = () => {
      const dpr = Math.min(window.devicePixelRatio, 2);
      const rect = canvas.getBoundingClientRect();

      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const centerX = canvas.width / 2 / window.devicePixelRatio;
      const centerY = canvas.height / 2 / window.devicePixelRatio - 12;

      ctx.strokeStyle = 'rgba(0,0,0,0.08)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(centerX, centerY, 150, 0, Math.PI * 2);
      ctx.stroke();

      const grad = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, 40);
      grad.addColorStop(0, '#FFD700');
      grad.addColorStop(1, '#FF9900');

      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(centerX, centerY, 40, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#0F1020';
      ctx.font = 'bold 16px Orbitron';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('MGO', centerX, centerY);

      satellites.forEach((sat) => {
        sat.angle += sat.speed;
        const x = centerX + Math.cos(sat.angle) * sat.radius;
        const y = centerY + Math.sin(sat.angle) * sat.radius;

        ctx.strokeStyle = 'rgba(0,0,0,0.12)';
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.lineTo(x, y);
        ctx.stroke();

        ctx.fillStyle = sat.color;
        ctx.beginPath();
        ctx.arc(x, y, 10, 0, Math.PI * 2);
        ctx.fill();
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, [isHydrated]);

  return (
    <section data-component-id="src/app/landing-page/components/HeroSection.tsx:114:4" data-component-path="src/app/landing-page/components/HeroSection.tsx" data-component-line="114" data-component-end-line="167" data-component-file="HeroSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22%5Cn%20%20%20%20%20%20%20%20relative%5Cn%20%20%20%20%20%20%20%20min-h-screen%5Cn%20%20%20%20%20%20%20%20flex%5Cn%20%20%20%20%20%20%20%20items-center%5Cn%20%20%20%20%20%20%20%20justify-center%5Cn%20%20%20%20%20%20%20%20overflow-hidden%5Cn%20%20%20%20%20%20%20%20-mt-32%5Cn%20%20%20%20%20%20%22%7D"
      className="
        relative
        min-h-screen
        flex
        items-center
        justify-center
        overflow-hidden
        -mt-32
      "
    >
      <div data-component-id="src/app/landing-page/components/HeroSection.tsx:125:6" data-component-path="src/app/landing-page/components/HeroSection.tsx" data-component-line="125" data-component-end-line="125" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20z-%5B1%5D%20bg-gradient-to-b%20from-transparent%20via-transparent%20to-white%2F80%22%7D" className="absolute inset-0 z-[1] bg-gradient-to-b from-transparent via-transparent to-white/80" />

      <canvas data-component-id="src/app/landing-page/components/HeroSection.tsx:127:6" data-component-path="src/app/landing-page/components/HeroSection.tsx" data-component-line="127" data-component-end-line="130" data-component-file="HeroSection.tsx" data-component-name="canvas" data-component-content="%7B%22elementName%22%3A%22canvas%22%2C%22className%22%3A%22absolute%20inset-0%20z-%5B2%5D%20pointer-events-none%22%7D"
        ref={canvasRef}
        className="absolute inset-0 z-[2] pointer-events-none"
      />

      <div data-component-id="src/app/landing-page/components/HeroSection.tsx:132:6" data-component-path="src/app/landing-page/components/HeroSection.tsx" data-component-line="132" data-component-end-line="166" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20z-10%20max-w-4xl%20mx-auto%20text-center%20px-4%22%7D" className="relative z-10 max-w-4xl mx-auto text-center px-4">
        <h1 data-component-id="src/app/landing-page/components/HeroSection.tsx:133:8" data-component-path="src/app/landing-page/components/HeroSection.tsx" data-component-line="133" data-component-end-line="135" data-component-file="HeroSection.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22font-orbitron%20font-bold%20text-5xl%20md%3Atext-7xl%20text-%5B%230F1020%5D%20mb-6%22%2C%22textContent%22%3A%22A%20Coordination%20Layer%20for%20Digital%20Networks%22%7D" className="font-orbitron font-bold text-5xl md:text-7xl text-[#0F1020] mb-6">
          A Coordination Layer for Digital Networks
        </h1>

        <p data-component-id="src/app/landing-page/components/HeroSection.tsx:137:8" data-component-path="src/app/landing-page/components/HeroSection.tsx" data-component-line="137" data-component-end-line="139" data-component-file="HeroSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-source-sans%20text-xl%20md%3Atext-2xl%20text-%5B%232A2C4A%5D%20mb-12%22%2C%22textContent%22%3A%22Infrastructure%2C%20research%20and%20ecosystem%20growth%20%E2%80%94%20aligned.%22%7D" className="font-source-sans text-xl md:text-2xl text-[#2A2C4A] mb-12">
          Infrastructure, research and ecosystem growth — aligned.
        </p>

        <div data-component-id="src/app/landing-page/components/HeroSection.tsx:141:8" data-component-path="src/app/landing-page/components/HeroSection.tsx" data-component-line="141" data-component-end-line="165" data-component-file="HeroSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20sm%3Aflex-row%20gap-4%20justify-center%22%7D" className="flex flex-col sm:flex-row gap-4 justify-center">
          <button data-component-id="src/app/landing-page/components/HeroSection.tsx:142:10" data-component-path="src/app/landing-page/components/HeroSection.tsx" data-component-line="142" data-component-end-line="157" data-component-file="HeroSection.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22%5Cn%20%20bg-yellow-400%5Cn%20%20text-black%5Cn%20%20font-rajdhani%20font-bold%5Cn%20%20py-4%20px-8%20rounded-lg%5Cn%20%20flex%20items-center%20gap-2%5Cn%20%20shadow-lg%20shadow-yellow-400%2F30%5Cn%20%20hover%3Abg-yellow-300%5Cn%20%20transition-all%5Cn%22%2C%22textContent%22%3A%22Explore%20Ecosystem%22%7D"
            onClick={onExploreClick}
            className="
  bg-yellow-400
  text-black
  font-rajdhani font-bold
  py-4 px-8 rounded-lg
  flex items-center gap-2
  shadow-lg shadow-yellow-400/30
  hover:bg-yellow-300
  transition-all
"
          >
            Explore Ecosystem
            <Icon data-component-id="src/app/landing-page/components/HeroSection.tsx:156:12" data-component-path="src/app/landing-page/components/HeroSection.tsx" data-component-line="156" data-component-end-line="156" data-component-file="HeroSection.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowRightIcon%22%7D" name="ArrowRightIcon" size={20} />
          </button>

          <button data-component-id="src/app/landing-page/components/HeroSection.tsx:159:10" data-component-path="src/app/landing-page/components/HeroSection.tsx" data-component-line="159" data-component-end-line="164" data-component-file="HeroSection.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22bg-black%2F80%20text-white%20font-rajdhani%20font-bold%20py-4%20px-8%20rounded-lg%22%2C%22textContent%22%3A%22View%20Initiatives%22%7D"
            onClick={onInvestmentClick}
            className="bg-black/80 text-white font-rajdhani font-bold py-4 px-8 rounded-lg"
          >
            View Initiatives
          </button>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
